<template>
  <v-row dense>
    <v-col
      v-if="$vuetify.display.lgAndUp"
      class="pl-0 pa-4 pt-0"
      cols="12"
      lg="4"
      md="4"
      sm="12"
      xl="4"
    >
      <SearchInput
        :search-path="'/' + currentRecord['fairsharingRecord'].id"
        :show-search-box="true"
      />
    </v-col>
    <v-col id="topElement" class="pa-0" cols="12" lg="8" md="12" sm="12" xl="8">
      <!--Filtered Chips-->
      <div
        v-if="getChips.length"
        class="d-flex align-content-center justify-content-center chips-holder"
      >
        <filter-chips />
      </div>
      <!--List Controller-->
      <ListController
        :options="{ hasPagination: true, hasSorting: false, hasListType: true }"
        class="mt-2"
        @change-list-type="changeListType"
      />
      <!--show filter button for tablet and below-->
      <div v-if="$vuetify.display.mdAndDown" class="mb-2">
        <v-btn class="bg-info" @click="showFiltersSM = true">
          <span class="mr-2">Show filters</span>
          <v-icon size="small"> fas fa-filter </v-icon>
        </v-btn>
      </div>
      <!-- Alert -->
      <v-alert v-if="getRecordsLength < 1 && !loading" border-color type="info">
        No records match your search!
      </v-alert>

      <!-- StackCard view -->
      <div
        :class="[
          'opacity-0-transition',
          { 'opacity-1-transition': !isColumnList },
        ]"
      >
        <article v-if="!isColumnList">
          <v-skeleton-loader :loading="loading" class="mt-2" type="image">
            <RecordsCardStack
              v-for="item in records"
              :key="'record_' + item.id"
              :record="item"
            />
            <!--Pagination-->
            <Pagination :total-pages="totalPages" class="mb-4" />
          </v-skeleton-loader>
        </article>
      </div>
      <!-- ColumnCard view -->
      <div
        :class="[
          'opacity-0-transition',
          { 'opacity-1-transition': isColumnList },
        ]"
      >
        <article v-if="isColumnList">
          <v-skeleton-loader :loading="loading" class="mt-2" type="image">
            <!-- ColumnCard view -->
            <v-row>
              <records-card-column
                v-for="item in records"
                :key="'record_' + item.id"
                :record="item"
              />
            </v-row>
            <!--Pagination-->
            <Pagination :total-pages="totalPages" class="mb-4" />
          </v-skeleton-loader>
        </article>
      </div>
    </v-col>
    <v-fade-transition>
      <div>
        <v-dialog v-model="showFiltersSM" :scrim="false" fullscreen scrollable>
          <v-card>
            <v-card-title class="bg-primary text-white pb-5">
              Add a filter
              <v-spacer />
              <v-btn size="x-small" @click="showFiltersSM = false">
                <v-icon>fas fa-times</v-icon>
              </v-btn>
            </v-card-title>
            <SearchInput class="pa-5" />
          </v-card>
        </v-dialog>
      </div>
    </v-fade-transition>
  </v-row>
</template>

<script>
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";

import FilterChips from "@/components/Records/Search/Header/FilterChips";
import ListController from "@/components/Records/Search/Header/ListController";
import Pagination from "@/components/Records/Search/Header/Pagination";
import SearchInput from "@/components/Records/Search/Input/SearchInput";
import RecordsCardColumn from "@/components/Records/Search/Output/RecordsCardColumn";
import RecordsCardStack from "@/components/Records/Search/Output/RecordsCardStack";
import filterChipsUtils from "@/utils/filterChipsUtils";
import stringUtils from "@/utils/stringUtils";

export default {
  name: "SearchCollection",
  components: {
    FilterChips,
    Pagination,
    ListController,
    SearchInput,
    RecordsCardColumn,
    RecordsCardStack,
  },
  mixins: [stringUtils, filterChipsUtils],
  data() {
    return {
      allowClicking: false,
      collectionIDs: [],
      receivedData: {},
      isColumnList: false,
      showFiltersSM: false,
      testEnvironment: false,
    };
  },
  computed: {
    ...mapState("record", ["currentRecord"]),
    ...mapState("records", [
      "records",
      "totalPages",
      "currentPage",
      "loading",
      "facets",
    ]),
    ...mapState("users", ["user"]),
    ...mapGetters("records", ["getRecordsLength"]),
    ...mapGetters("introspection", ["buildQueryParameters"]),
    currentPath: function () {
      const _module = this;
      let queryParams = {};
      Object.keys(this.$route.query).forEach(function (prop) {
        let queryVal = _module.$route.query[prop];
        if (queryVal) {
          queryParams[prop] = decodeURI(queryVal);
        }
      });
      const title = "Collection";
      return [title, queryParams];
    },
  },
  watch: {
    currentPath: async function () {
      try {
        // testEnvironment variable is only for test case.
        if (this.testEnvironment)
          throw new Error("an error occurred while fetching data");
        this.scrollTo();
        let returnedQuery = this.buildQueryParameters(this.currentPath);
        this.showFiltersSM = false;
        await this.fetchCollectionRecords({
          params: returnedQuery,
          token: this.user().credentials.token,
        });
      }
      catch (e) {
        this.errors = e.message;
      }
    },
  },
  async mounted() {
    await this.prepareCollectionData();
    // make the left panel sticky under any circumstances.
    this.setGeneralUIAttributesAction({
      headerVisibilityState: false,
    });
  },
  beforeUnmount() {
    this.cleanRecordsStore();
  },
  methods: {
    ...mapActions("records", [
      "initializeCollectionRecords",
      "fetchCollectionRecords",
    ]),
    ...mapMutations("records", ["cleanRecordsStore"]),
    ...mapActions("uiController", ["setGeneralUIAttributesAction"]),

    scrollTo() {
      this.$scrollTo("#topElement", 1000, {
        easing: "ease-out",
      });
    },

    changeListType: function (listType) {
      this.isColumnList = listType;
    },
    async prepareCollectionData() {
      let returnedQuery;
      try {
        if (
          Object.keys(this.currentRecord["fairsharingRecord"]).includes(
            "recordAssociations",
          )
        ) {
          const collections = this.prepareAssociations(
            this.currentRecord["fairsharingRecord"]["recordAssociations"],
            [],
          ).filter((item) => item.recordAssocLabel === "collects");
          collections.forEach((item) => {
            this.collectionIDs.push(item.id);
          });
          this.errors = false;
          returnedQuery = this.buildQueryParameters(this.currentPath);
          await this.initializeCollectionRecords(this.collectionIDs);
        }
        else {
          return false;
        }
      }
      catch (e) {
        this.errors = e.message;
      }
      try {
        await this.fetchCollectionRecords({
          params: returnedQuery,
          token: this.user().credentials.token,
        });
      }
      catch (e) {
        this.errors = e.message;
      }
    },
    prepareAssociations(associations, reverseAssociations) {
      let _module = this;
      let recordAssociations = [];
      let joinedArrays = associations.concat(reverseAssociations);
      const properties = ["fairsharingRecord", "linkedRecord"];

      joinedArrays.forEach((item) => {
        let object = {};
        properties.forEach((prop) => {
          if (Object.prototype.hasOwnProperty.call(item, prop)) {
            object.recordAssocLabel = _module.cleanString(
              item.recordAssocLabel,
            );
            object.id = item[prop].id;
            object.registry = item[prop].registry;
            object.name = item[prop].name;
            object.subject = _module.currentRecord["fairsharingRecord"].name;
            object.type = item[prop].type;
          }
        });
        recordAssociations.push(object);
      });
      return recordAssociations;
    },
  },
};
</script>

<style scoped>
.chips-holder {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f5f5f5;
  min-height: 50px;
  border: #dbdbdb dotted 2px;
  border-radius: 10px;
  -moz-border-radius: 10px;
  -webkit-border-radius: 10px;
}
</style>
